import { ArrowLeft, Check, ChevronRight, ClipboardCheck, Flag, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError } from '../../lib/api.ts';
import type { PassagesSeance } from '../../lib/planning.ts';
import { getPassages } from '../../lib/planning.ts';
import { modeLabel } from '../../lib/modes.ts';

const jourCourt = (iso: string): string => {
    const d = new Date(iso);
    return `${d.getDate()}/${d.getMonth() + 1}`;
};

const jourLong = (iso: string): string =>
    new Date(iso).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

/* Les passages d'une séance, atelier par atelier.
 *
 * ── Ce que le bilan ne montrait pas ─────────────────────────────────────────
 *
 * Il compare une exécution à la précédente, deux à deux. C'est assez pour dire
 * « mieux qu'hier », jamais pour montrer une progression — et la liste des
 * séances ne pointait que vers le dernier bilan, les autres étant introuvables.
 *
 * Un passage d'examen se juge sur la suite de tous les autres, et surtout sur
 * l'atelier qui bloque : le bunker à 2/5 trois fois de suite, quand le putting
 * est acquis depuis longtemps. C'est cette ligne-là qu'on vient chercher, pas
 * un total.
 *
 * ── Le total en haut, le détail en dessous ──────────────────────────────────
 *
 * Une rangée de bâtons pour les totaux — la même que l'évolution d'un club,
 * mêmes couleurs, même lecture — puis une ligne par atelier avec ses points à
 * chaque passage. Le total dit si l'on progresse, le détail dit par où.
 *
 * ── Le plus récent à DROITE ─────────────────────────────────────────────────
 *
 * Le serveur rend les passages du plus récent au plus ancien, parce que c'est
 * l'ordre d'une liste. Une progression, elle, se lit de gauche à droite comme
 * le temps : on retourne donc la suite ici, une fois pour toutes, plutôt que
 * dans chacune des deux figures.
 */
export function Passages() {
    const { workoutId } = useParams<{ workoutId: string }>();
    const [data, setData] = useState<PassagesSeance | null | undefined>(undefined);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!workoutId) return;
        void getPassages(workoutId)
            .then(setData)
            .catch((e) => {
                setError(e instanceof ApiError ? e.message : 'Chargement impossible');
                setData(null);
            });
    }, [workoutId]);

    if (data === undefined) {
        return (
            <div className="flex justify-center py-16">
                <span className="loading loading-ring loading-lg text-primary" />
            </div>
        );
    }

    // Du plus ancien au plus récent : le sens du temps.
    const passages = data ? [...data.passages].reverse() : [];
    const etapes = data?.etapes ?? [];

    /** Les points d'un passage à un rang donné, et son maximum. */
    const cellule = (
        p: PassagesSeance['passages'][number],
        position: number,
    ) => p.etapes.find((e) => e.position === position) ?? null;

    /** Le total d'un passage sur les ateliers notés en points. */
    const total = (p: PassagesSeance['passages'][number]): number =>
        p.etapes.reduce((n, e) => n + (e.points ?? 0), 0);

    /** Le maximum possible, celui du plan : il ne dépend pas du passage. */
    const totalMax = etapes.reduce((n, e) => n + (e.pointsMax ?? 0), 0);
    const plusHaut = Math.max(1, totalMax, ...passages.map(total));

    /** L'objectif du plan, quand il y en a un. */
    const objectif = data?.workout.pointsTarget ?? null;

    /* Sans objectif, trois paliers : c'est une appréciation, faute de mieux.
     * Avec un objectif, il n'y a plus d'appréciation à porter — on est au-dessus
     * ou en dessous, et un palier intermédiaire laisserait croire qu'un
     * « presque » existe le jour de l'examen. */
    const teinte = (part: number): string => (part >= 0.7 ? 'var(--color-primary)'
        : part >= 0.5 ? 'var(--brass-ink)'
            : 'var(--brique-bord)');

    const teinteTotal = (points: number): string => (objectif === null
        ? teinte(points / (totalMax || 1))
        : points >= objectif ? 'var(--color-primary)' : 'var(--brique-bord)');

    const nombre = (v: number): string =>
        v.toLocaleString('fr-FR', { maximumFractionDigits: 1 });

    /* Le verdict d'un passage : le total ET le parcours. Le seul total suffisait
     * à colorer un bâton, pas à dire « test validé » — vingt-six points sans
     * parcours validé ne valent pas la carte verte. */
    const rangsParcours = etapes.filter((e) => e.kind === 'course').map((e) => e.position);
    const valide = (p: PassagesSeance['passages'][number]): boolean | null => {
        if (objectif === null) return null;
        const parcoursOk = rangsParcours.every(
            (r) => p.etapes.find((e) => e.position === r)?.passed === true,
        );
        return total(p) >= objectif && parcoursOk;
    };

    return (
        <div className="space-y-4 py-2">
            <Link to="/entrainement/seances" className="btn btn-ghost btn-sm gap-2">
                <ArrowLeft size={16} /> Mes séances
            </Link>

            <header>
                <p className="text-xs font-bold uppercase tracking-[0.16em] opacity-55">
                    Mes passages
                </p>
                <h1 className="font-display text-2xl font-bold tracking-tight">
                    {data?.workout.name ?? 'Séance'}
                </h1>
            </header>

            {error && <div role="alert" className="alert alert-error text-sm"><span>{error}</span></div>}

            {passages.length === 0 ? (
                <p className="rounded-box border border-dashed border-base-300 p-8 text-center text-sm opacity-60">
                    Aucun passage pour l’instant. Lancez la séance, et vous retrouverez
                    ici chacun de vos essais.
                </p>
            ) : (
                <>
                    {totalMax > 0 && (
                        <section className="rounded-box border border-base-300 bg-base-100 p-3 shadow-sm">
                            <h2 className="px-0.5 text-[11px] font-bold uppercase tracking-[0.14em]"
                                style={{ color: 'var(--tuile-encre)' }}>
                                Total, passage après passage
                            </h2>
                            <div className="relative mt-2 flex h-24 items-stretch gap-1">
                                {passages.map((p, i) => {
                                    const t = total(p);
                                    return (
                                        <div key={p.id} className="flex min-w-0 flex-1 flex-col justify-end gap-0.5">
                                            {/* Le nombre découpe la ligne d'objectif au lieu
                                                d'être barré par elle : un total juste sous le
                                                seuil se lit précisément là où les deux se
                                                croisent. */}
                                            <span className="relative z-10 mx-auto rounded-[2px] px-1 text-center
                                                             text-[10px] font-bold leading-none tabular-nums opacity-70"
                                                style={{ background: 'var(--color-base-100)' }}>
                                                {t}
                                            </span>
                                            <div className="w-full rounded-t-[3px]"
                                                style={{
                                                    height: `${Math.max(3, (t / plusHaut) * 100)}%`,
                                                    background: teinteTotal(t),
                                                    opacity: i === passages.length - 1 ? 1 : 0.55,
                                                }} />
                                        </div>
                                    );
                                })}
                                {/* L'objectif, en travers des bâtons. Un total ne
                                    dit rien seul — trente-six points, c'est beaucoup
                                    ou peu selon où passe la barre — et la lire dans
                                    une phrase sous le graphique obligeait à comparer
                                    deux nombres de tête à chaque passage. */}
                                {objectif !== null && objectif <= plusHaut && (
                                    /* Tirets laiton ET blancs, en alternance. Un trait
                                       d'une seule teinte se perdait forcément quelque
                                       part : sombre, il disparaît sur le vert d'un
                                       passage réussi ; clair, sur le papier de la
                                       carte. En plein soleil, il faut que l'un des
                                       deux ressorte partout où la ligne passe. */
                                    <div className="pointer-events-none absolute inset-x-0 h-[2px]"
                                        style={{
                                            bottom: `${(objectif / plusHaut) * 100}%`,
                                            background: 'repeating-linear-gradient(90deg, '
                                                + 'var(--brass-ink) 0 6px, '
                                                + 'rgb(255 255 255 / 0.8) 6px 12px)',
                                        }} />
                                )}
                            </div>
                            <div className="mt-1 flex gap-1 text-[9px] opacity-55">
                                {passages.map((p) => (
                                    <span key={p.id}
                                        className="min-w-0 flex-1 overflow-hidden text-center tabular-nums">
                                        {jourCourt(p.startedAt)}
                                    </span>
                                ))}
                            </div>
                            {/* Le chiffre de l'objectif est ICI et non sur la ligne :
                                collé à elle, il tombait sur le bâton le plus haut dès
                                qu'un passage dépassait le seuil — c'est-à-dire précisément
                                le jour où l'on veut le lire. Le trait témoin fait le lien. */}
                            <p className="mt-1.5 flex items-center justify-center gap-1.5 text-xs opacity-60">
                                {objectif === null ? (
                                    <>sur <span className="font-score tabular-nums">{totalMax}</span> points possibles</>
                                ) : (
                                    <>
                                        <span className="inline-block h-[2px] w-5 align-middle"
                                            style={{
                                                background: 'repeating-linear-gradient(90deg, '
                                                    + 'var(--brass-ink) 0 6px, '
                                                    + 'rgb(255 255 255 / 0.8) 6px 12px)',
                                            }} aria-hidden="true" />
                                        objectif : <span className="font-score tabular-nums">{nombre(objectif)}</span>
                                        {' '}points sur <span className="font-score tabular-nums">{totalMax}</span>
                                    </>
                                )}
                            </p>
                        </section>
                    )}

                    {/* Le détail : une ligne par atelier, une colonne par passage.
                        C'est là que se voit l'atelier qui ne bouge pas. */}
                    <section className="rounded-box border border-base-300 bg-base-100 p-3 shadow-sm">
                        <h2 className="px-0.5 text-[11px] font-bold uppercase tracking-[0.14em]"
                            style={{ color: 'var(--tuile-encre)' }}>
                            Atelier par atelier
                        </h2>
                        <div className="mt-2 space-y-1.5">
                            {etapes.map((e) => (
                                <div key={e.position} className="flex items-center gap-2">
                                    <span className="min-w-0 flex-1 truncate text-xs font-semibold">
                                        {e.name ?? (e.isPutting ? 'Putting' : modeLabel(e.mode ?? ''))}
                                        {/* Le barème collé au nom, et non en légende
                                            sous le tableau : là-bas il fallait
                                            réassocier huit noms à huit nombres, et la
                                            légende faisait trois lignes pour une
                                            information qui tient en deux caractères. */}
                                        {e.pointsMax != null && (
                                            <span className="ml-1 font-normal opacity-45">/{e.pointsMax}</span>
                                        )}
                                    </span>
                                    <span className="flex shrink-0 gap-1">
                                        {passages.map((p) => {
                                            const c = cellule(p, e.position);
                                            // Le parcours ne rend pas de points : un
                                            // oui ou un non, dit par une marque.
                                            if (e.kind === 'course') {
                                                return (
                                                    <span key={p.id}
                                                        className="flex size-7 items-center justify-center rounded-field text-xs"
                                                        style={c?.passed == null
                                                            ? { background: 'color-mix(in oklab, var(--tuile-encre) 8%, transparent)' }
                                                            : c.passed
                                                                ? { background: 'var(--color-primary)', color: 'var(--color-primary-content)' }
                                                                : { background: 'var(--brique)', color: 'var(--brique-encre)' }}>
                                                        {c?.passed == null ? '·'
                                                            : c.passed ? <Check size={14} strokeWidth={3} />
                                                                : <X size={14} strokeWidth={3} />}
                                                    </span>
                                                );
                                            }
                                            const max = e.pointsMax ?? 0;
                                            const pts = c?.points ?? null;
                                            return (
                                                <span key={p.id}
                                                    className="font-score flex size-7 items-center justify-center
                                                               rounded-field text-xs tabular-nums"
                                                    style={pts == null
                                                        ? { background: 'color-mix(in oklab, var(--tuile-encre) 8%, transparent)', opacity: 0.5 }
                                                        : {
                                                            background: teinte(max > 0 ? pts / max : 0),
                                                            color: '#fff',
                                                        }}>
                                                    {pts ?? '·'}
                                                </span>
                                            );
                                        })}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Chaque passage, pour rouvrir son bilan. */}
                    <section className="space-y-2">
                        {[...passages].reverse().map((p) => (
                            <Link key={p.id} to={`/entrainement/bilan/${p.id}`}
                                className="flex items-center gap-3 rounded-box bg-base-200 p-3 transition-colors hover:bg-base-300">
                                <Flag size={16} className="shrink-0 opacity-50" aria-hidden="true" />
                                <span className="min-w-0 flex-1">
                                    <span className="block truncate text-sm font-semibold">
                                        {jourLong(p.startedAt)}
                                    </span>
                                    <span className="block text-xs opacity-60">
                                        {total(p)} point{total(p) > 1 ? 's' : ''}
                                        {totalMax > 0 && ` sur ${totalMax}`}
                                        {p.status !== 'completed' && ' · interrompu'}
                                    </span>
                                </span>
                                {/* Le verdict, et non le seul total : c'est la
                                    réponse à « est-ce que je l'ai eu, cette fois-là ? » */}
                                {valide(p) !== null && (
                                    <span className="shrink-0 rounded-field px-2 py-1 text-[11px] font-bold"
                                        style={valide(p)
                                            ? { background: 'var(--score-gagne)', color: 'var(--fairway-deep)' }
                                            : { background: 'var(--brique)', color: 'var(--brique-encre)' }}>
                                        {valide(p) ? 'Validé' : 'Non validé'}
                                    </span>
                                )}
                                <ChevronRight size={18} className="shrink-0 opacity-40" aria-hidden="true" />
                            </Link>
                        ))}
                    </section>

                    {/* Ce qui reste à faire ailleurs, tout à la fin.
                        Atteindre l'objectif ici ne donne pas la carte verte, et
                        un joueur qui voit trente-six sur quarante-cinq en vert
                        n'a aucune raison de deviner qu'il manque un examen passé
                        sur une table, sans un club. */}
                    {data?.workout.completionNote && (
                        <section className="flex gap-3 rounded-box border p-3"
                            style={{
                                borderColor: 'var(--tuile)',
                                background: 'color-mix(in oklab, var(--tuile) 35%, transparent)',
                            }}>
                            <ClipboardCheck size={18} className="mt-0.5 shrink-0"
                                style={{ color: 'var(--tuile-encre)' }} aria-hidden="true" />
                            <p className="text-xs leading-relaxed" style={{ color: 'var(--tuile-encre)' }}>
                                {data.workout.completionNote}
                            </p>
                        </section>
                    )}
                </>
            )}
        </div>
    );
}
